var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import cx from 'classnames';
import { PositioningPortal } from '@move/react-positioning-portal';
import { ButtonSize } from '@move-ui/button';
import Text, { TextSize } from '@move-ui/text';
import { ChevronSmallFilledIcon } from '@move-ui/icons-asset-catalog';
import { flatMap } from '../utils/arrayFunctions';
import { getMenuPosition } from './utils/getMenuPosition';
import { Menu } from './Menu';
import { Option } from './Option';
import { MenuDirection } from './MenuDirection';
import styles from './DropdownButton.module.css';
import { stopEvent } from '../utils/stopEvent';
var ChevronIconSize;
(function (ChevronIconSize) {
    ChevronIconSize[ChevronIconSize["Small"] = 8] = "Small";
    ChevronIconSize[ChevronIconSize["Large"] = 10] = "Large";
})(ChevronIconSize || (ChevronIconSize = {}));
export var DropdownButton = function (_a) {
    var _b, _c, _d;
    var testId = _a["data-testid"], Button = _a.button, className = _a.className, disabled = _a.disabled, hideChevron = _a.hideChevron, id = _a.id, isFullWidth = _a.isFullWidth, label = _a.label, menuClassName = _a.menuClassName, onChange = _a.onChange, onClick = _a.onClick, onClose = _a.onClose, onOpen = _a.onOpen, options = _a.options, _e = _a.preferredAutoDirection, preferredAutoDirection = _e === void 0 ? MenuDirection.Left : _e, sections = _a.sections, _f = _a.size, size = _f === void 0 ? ButtonSize.Size200 : _f, value = _a.value, rest = __rest(_a, ['data-testid', "button", "className", "disabled", "hideChevron", "id", "isFullWidth", "label", "menuClassName", "onChange", "onClick", "onClose", "onOpen", "options", "preferredAutoDirection", "sections", "size", "value"]);
    var buttonRef = useRef();
    var _g = useState(false), isOpen = _g[0], setIsOpen = _g[1];
    var _h = useState(false), isPortalClosedWithEscapeKey = _h[0], setIsPortalClosedWithEscapeKey = _h[1];
    useEffect(function () {
        import('focus-visible').catch(function () { return null; });
    }, []);
    useEffect(function () {
        if (sections && options) {
            // eslint-disable-next-line no-console
            console.error("DropdownButton ".concat(id, ": options and sections params can't be used together. Sections will take priority over options. Please remove options prop."));
        }
    }, [sections, options, id]);
    var toggle = useCallback(function () {
        setIsOpen(!isOpen);
        if (isOpen) {
            onClose === null || onClose === void 0 ? void 0 : onClose();
        }
        else {
            onOpen === null || onOpen === void 0 ? void 0 : onOpen();
        }
    }, [isOpen, onOpen, onClose]);
    var close = useCallback(function () {
        var _a;
        setIsOpen(false);
        onClose === null || onClose === void 0 ? void 0 : onClose();
        if (isPortalClosedWithEscapeKey) {
            setIsPortalClosedWithEscapeKey(false);
            (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
        }
    }, [onClose, isPortalClosedWithEscapeKey]);
    var optionsWithoutSections = useMemo(function () {
        return (sections === null || sections === void 0 ? void 0 : sections.length) ? flatMap(sections, function (_a) {
            var values = _a.values;
            return values;
        }) : options;
    }, [options, sections]);
    var selectedLabel = useMemo(function () { var _a; return (_a = optionsWithoutSections === null || optionsWithoutSections === void 0 ? void 0 : optionsWithoutSections.find(function (option) { return value === option.value; })) === null || _a === void 0 ? void 0 : _a.label; }, [optionsWithoutSections, value]);
    var optionList = useMemo(function () {
        return optionsWithoutSections === null || optionsWithoutSections === void 0 ? void 0 : optionsWithoutSections.map(function (option) {
            var _a, _b;
            return (React.createElement(Option, __assign({ key: option.value || 'empty' }, option, { onClick: function (event) {
                    var _a;
                    stopEvent(event);
                    close();
                    if (event.type === 'keydown') {
                        (_a = buttonRef.current) === null || _a === void 0 ? void 0 : _a.focus();
                    }
                    onChange(option.value);
                }, selected: value === ((_a = option.value) !== null && _a !== void 0 ? _a : ''), sectionLabel: (_b = sections === null || sections === void 0 ? void 0 : sections.find(function (_a) {
                    var _b;
                    var values = _a.values;
                    return ((_b = values[0]) === null || _b === void 0 ? void 0 : _b.value) === option.value;
                })) === null || _b === void 0 ? void 0 : _b.label })));
        });
    }, [close, onChange, sections, optionsWithoutSections, value]);
    useEffect(function () {
        var handleResize = function () {
            if (isOpen) {
                close();
            }
        };
        window.addEventListener('resize', handleResize);
        return function () {
            window.removeEventListener('resize', handleResize);
        };
    }, [isOpen, close]);
    var handleKeyDown = useCallback(function (event) {
        switch (event.key) {
            case 'ArrowUp':
            case 'ArrowDown':
            case 'Enter':
            case ' ':
                toggle();
                stopEvent(event);
                break;
            default:
                break;
        }
    }, [toggle]);
    var handleClick = useCallback(function (event) {
        toggle();
        onClick === null || onClick === void 0 ? void 0 : onClick(event);
    }, [toggle, onClick]);
    var handleCloseOnKeyDown = useCallback(function (event) {
        var isEscapeKey = event.key === 'Escape';
        setIsPortalClosedWithEscapeKey(isEscapeKey);
        return isEscapeKey;
    }, []);
    return (React.createElement(PositioningPortal, { isOpen: isOpen, onShouldClose: close, closeOnOutsideClick: true, closeOnKeyDown: handleCloseOnKeyDown, positionStrategy: getMenuPosition(preferredAutoDirection), portalElement: React.createElement("div", { style: { zIndex: 1 } }), portalContent: function (_a) {
            var strategy = _a.strategy, isPositioned = _a.isPositioned;
            return (React.createElement(Menu, { isOpen: isPositioned, position: strategy, "data-testid": testId, className: menuClassName }, optionList));
        } },
        React.createElement(Button, __assign({}, rest, { ref: buttonRef, "data-testid": testId, "data-value": value, id: id, size: size, disabled: disabled, iconSize: size, fontSize: size, onClick: handleClick, onKeyDown: handleKeyDown, "aria-haspopup": "menu", "aria-expanded": isOpen, className: cx(styles.button, className, (_b = {},
                _b[styles.ghost] = Button.displayName === 'GhostButton',
                _b[styles.tertiary] = Button.displayName === 'TertiaryButton',
                _b.active = isOpen,
                _b[styles.fullWidth] = isFullWidth,
                _b)), label: label ? React.createElement("span", { className: styles.label }, label) : null }),
            React.createElement(Text, { className: cx(styles.text, (_c = {},
                    _c[styles.fullWidth] = isFullWidth,
                    _c)), size: size === ButtonSize.Size100 ? TextSize.XXS : TextSize.XS }, selectedLabel),
            !hideChevron && (React.createElement(ChevronSmallFilledIcon, __assign({ width: size === ButtonSize.Size100
                    ? ChevronIconSize.Small
                    : ChevronIconSize.Large, height: size === ButtonSize.Size100
                    ? ChevronIconSize.Small
                    : ChevronIconSize.Large }, (testId && { 'data-testid': "".concat(testId, "ChevronIcon") }), { className: cx(styles.icon, (_d = {}, _d[styles.open] = isOpen, _d)) }))))));
};
//# sourceMappingURL=DropdownButton.js.map